import React from 'react';
import {
  Section,
  Container,
  SectionHeader,
  ServiceCard,
  QuoteBlock,
  ContactForm,
} from '../components';
import {
  heroContent,
  services,
  approach,
  quote,
  contactContent,
  siteConfig,
} from '../data/content';

const HomePage = ({ onNavigate }) => {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Hero Section */}
      <Section className="min-h-screen flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-stone-100 via-stone-50 to-amber-50/30" />
        <div className="absolute top-20 right-10 w-96 h-96 bg-amber-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-72 h-72 bg-stone-200/40 rounded-full blur-3xl" />

        <Container className="relative pt-32 pb-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Text Content */}
            <div className="order-2 lg:order-1">
              <p className="font-body text-sm tracking-[0.3em] uppercase text-amber-700 mb-6 opacity-0 animate-fade-up">
                {heroContent.tagline}
              </p>

              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-light leading-tight text-stone-800 mb-4 opacity-0 animate-fade-up delay-100">
                {heroContent.title}
                <span className="block italic text-amber-800">{heroContent.titleEmphasis}</span>
              </h1>

              <p className="font-display text-xl md:text-2xl text-stone-600 mb-8 opacity-0 animate-fade-up delay-200">
                {heroContent.subtitle}
              </p>

              {/* Founder Highlight */}
              <div className="bg-white/60 backdrop-blur-sm border-l-2 border-amber-300 pl-6 py-4 mb-10 opacity-0 animate-fade-up delay-300">
                <p
                  className="font-body text-stone-600 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: heroContent.founderHighlight }}
                />
                <button
                  onClick={() => onNavigate('about')}
                  className="font-body text-sm text-amber-700 hover:text-amber-800 mt-3 inline-flex items-center gap-2 transition-colors"
                >
                  Learn more about Dr. Mundy
                  <span>→</span>
                </button>
              </div>

              <div className="flex flex-wrap gap-4 opacity-0 animate-fade-up delay-400">
                <button onClick={() => scrollTo('contact')} className="btn-primary">
                  {heroContent.primaryCta}
                </button>
                <button onClick={() => scrollTo('services')} className="btn-secondary">
                  {heroContent.secondaryCta}
                </button>
              </div>
            </div>

            {/* Image */}
            <div className="order-1 lg:order-2 opacity-0 animate-fade-in delay-400">
              <img
                src="/images/linda-mundy.jpeg"
                alt="Dr. Linda Mundy"
                className="aspect-[4/5] object-cover rounded-sm shadow-2xl"
              />
            </div>
          </div>
        </Container>
      </Section>

      {/* Services Section */}
      <Section id="services" className="py-32 bg-stone-100">
        <Container>
          <SectionHeader
            tag="Services"
            title = "Opportunities to Work"
            titleEmphasis="Together"
            centered
          />
          <div>
            <p className="font-body text-stone-600 leading-relaxed mb-16 text-center max-w-2xl mx-auto">
              Physical, Emotional, Mental, and Spiritual Health & Wellness
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, i) => (
              <ServiceCard key={i} {...service} />
            ))}
          </div>
        </Container>
      </Section>

      {/* Approach Section */}
      <Section className="py-32 bg-stone-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500 to-transparent" />
        </div>

        <Container className="relative">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <p className="font-body text-sm tracking-[0.3em] uppercase text-amber-400 mb-4">
                Approach
              </p>
              <h2 className="font-display text-4xl md:text-5xl font-light mb-8">
                {approach.title}
                <span className="block italic text-amber-300">{approach.titleEmphasis}</span>
              </h2>
              <div className="space-y-6 font-body text-stone-300 leading-relaxed">
                {approach.description.map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              {approach.steps.map((step, i) => (
                <div key={i} className="flex gap-6 group">
                  <span className="font-display text-3xl text-amber-400/40 group-hover:text-amber-400 transition-colors">
                    {step.num}
                  </span>
                  <div>
                    <h4 className="font-display text-xl text-white mb-1">{step.title}</h4>
                    <p className="font-body text-sm text-stone-400">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Quote Section */}
      <QuoteBlock text={quote.text} author={quote.author} />

      {/* Contact Section */}
      <Section id="contact" className="py-32 bg-white">
        <Container>
          <div className="grid lg:grid-cols-2 gap-20">
            <div>
              <p className="section-tag">Connect</p>
              <h2 className="section-title mb-8">
                {contactContent.title}
                <span className="block italic text-amber-800">{contactContent.titleEmphasis}</span>
              </h2>
              <p className="font-body text-stone-600 leading-relaxed mb-10">
                {contactContent.description}
              </p>

              <div className="space-y-6">
                <div>
                  <p className="font-body text-xs tracking-widest uppercase text-stone-400 mb-1">
                    Email
                  </p>
                  <a
                    href={`mailto:${siteConfig.email}`}
                    className="font-body text-stone-800 hover:text-amber-700 transition-colors"
                  >
                    {siteConfig.email}
                  </a>
                </div>
                <div>
                  <p className="font-body text-xs tracking-widest uppercase text-stone-400 mb-1">
                    Location
                  </p>
                  <p className="font-body text-stone-800">{siteConfig.location}</p>
                </div>
                <div>
                  <p className="font-body text-xs tracking-widest uppercase text-stone-400 mb-1">
                    Connect
                  </p>
                  <a
                    href={siteConfig.linkedIn}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-body text-stone-800 hover:text-amber-700 transition-colors"
                  >
                    LinkedIn →
                  </a>
                </div>
              </div>
            </div>

            <ContactForm />
          </div>
        </Container>
      </Section>
    </>
  );
};

export default HomePage;
